import { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import Helmet from 'react-helmet';
import Lottie from 'react-lottie';
import loadable from '@loadable/component';

import { Page } from '@zillow/constellation';

import * as S from './styles';

import { getCanAccessCookieValue } from 'app/shared/utils/cookieUtils';

import bethOnLaptop from 'images/zrmLogin/beth-on-laptop.json';
import houseWithLessHills from 'images/zrmLogin/houseWithLessHills.png';
import easyToBeLandlordBanner from 'images/zrmLogin/easyToBeLandlordBanner.png';
import houseWithMoreHills from 'images/zrmLogin/houseWithMoreHills.png';
import startCreatingListingsBanner from 'images/zrmLogin/startCreatingListingsBanner.png';

import StringConstants from 'app/shared/constants/StringConstants';
import FeatureFlagConstants from 'app/shared/constants/FeatureFlagConstants';

import ZillowWebLogin from 'app/shared/components/ZillowWebLogin';

import type { State } from 'app/shared/types/State';

const RegLogin = loadable(() => import('@zillow/reg-login'));

enum Variants {
  DEFAULT = 'default',
  MULTI_FAMILY = 'multifamily',
}

const ZRMLoginPage = () => {
  const canAccessZRMAuth0 = useMemo(() => getCanAccessCookieValue(FeatureFlagConstants.ZRM_AUTH_0_ENABLED), []);

  const { loggedIn, pathname, urlParameters } = useSelector(
    (state: State) => ({
      loggedIn: state?.user?.loggedIn,
      pathname: state.router.location.pathname,
      urlParameters: state.router.location.query,
    }),
    shallowEqual,
  );

  const redirectUrl = urlParameters?.url ? decodeURIComponent(urlParameters.url) : '/rental-manager/properties';

  const variant = urlParameters.variant ?? Variants.DEFAULT;
  const [isClientMounted, setIsClientMounted] = useState(false);

  // RegLogin can only be mounted after the client loads.
  useEffect(() => {
    setIsClientMounted(true);
  }, []);

  useEffect(() => {
    // @ts-expect-error
    import('@zillow/reg-login/src/less/reg-login.less');
  }, []);

  if (loggedIn) {
    if (typeof window !== 'undefined') {
      window.location.href = '/rental-manager/';
    }

    return null;
  }

  return (
    <Page backgroundColor="white">
      <Helmet>
        <title>Please sign in | Zillow Rental Manager</title>
        <meta name="description" content="Sign in or Register | Zillow Rental Manager." />
        <link rel="canonical" href={`${StringConstants.ZILLOW_URL}${pathname}`} />
      </Helmet>

      {isClientMounted && (
        <S.FlexContainer display="flex" justifyContent="space-between" flexDirection={{ default: 'column', md: 'row' }}>
          <S.MobileWebImage alt="" src={houseWithLessHills} />

          {canAccessZRMAuth0 ? (
            <ZillowWebLogin redirectUrl={redirectUrl} />
          ) : (
            <S.RegLoginContainer>
              {/* See https://gitlab.zgtools.net/zillow/authentication/reg-login/-/tree/master/packages/reg-login for supported props */}
              {/* Also https://gitlab.zgtools.net/zillow/authentication/reg-login/-/blob/master/packages/reg-login/src/containers/App.js#L50 */}
              <RegLogin
                entryPoint="rentals-manager-login"
                facebookAppId="172285552816089"
                googleClientId="238648973530.apps.googleusercontent.com"
                headerText="Sign up for Zillow Rental Manager"
                isNativeApp={true}
                redirectUrl={redirectUrl}
                showProRegCheckbox={false}
              />
            </S.RegLoginContainer>
          )}

          {variant === Variants.DEFAULT && (
            <S.DefaultGraphicContainer>
              <S.EasyToBeLandlordBanner
                src={easyToBeLandlordBanner}
                alt="It's easier than ever to be a landlord. Save time with our property management
                                tools that help you get what you need - signed leases and rent payments."
              />
              <S.BottomAlignedImage src={houseWithMoreHills} alt="" />
            </S.DefaultGraphicContainer>
          )}

          {variant === Variants.MULTI_FAMILY && (
            <S.MultifamilyGraphicContainer>
              <S.StartCreatingListingsBanner
                src={startCreatingListingsBanner}
                alt="Start creating listings to access Zillow's renter network."
              />
              <S.BethOnLaptopContainer>
                <Lottie options={{ animationData: bethOnLaptop, loop: false }} />
              </S.BethOnLaptopContainer>
            </S.MultifamilyGraphicContainer>
          )}
        </S.FlexContainer>
      )}
    </Page>
  );
};

export default ZRMLoginPage;
